import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// Translation resources for navigation + dashboard + login + common CTAs.
// Each language uses the same keys; rich forms remain English (acceptable for v1).
const resources = {
  en: {
    translation: {
      app: {
        tagline: "Unified Loan Origination System",
        loading: "Loading…",
        cancel: "Cancel",
        save: "Save",
        submit: "Submit",
        confirm: "Confirm",
        delete: "Delete",
        edit: "Edit",
        search: "Search",
        download: "Download",
        upload: "Upload",
        close: "Close",
        next: "Next",
        previous: "Previous",
        new: "New",
        actions: "Actions",
        status: "Status",
        active: "Active",
        disabled: "Disabled",
      },
      login: {
        title: "Welcome back",
        subtitle: "Sign in to your account",
        email: "Email",
        password: "Password",
        signin: "Sign in",
        forgot: "Forgot password?",
        demo: "Demo accounts",
      },
      nav: {
        operations: "Operations",
        governance: "Governance",
        insights: "Insights",
        administration: "Administration",
        dashboard: "Dashboard",
        customers: "Customers",
        applications: "Applications",
        drafts: "My Drafts",
        reportBuilder: "Report Builder",
        foQueue: "FO Queue",
        approvals: "Approvals",
        fieldVerification: "Field Verification",
        documents: "Documents",
        masterData: "Master Data",
        users: "Users",
        settings: "Settings",
        rules: "Policy Rules",
        themes: "Themes",
        notifications: "Notifications",
        superAdmin: "Super Admin",
        tenantOverview: "Tenant Overview",
        onboardBank: "Onboard Bank",
        allUsers: "All Users",
        appearance: "Appearance",
      },
      dashboard: {
        welcome: "Welcome",
        totalApplications: "Total Applications",
        inProgress: "In Progress",
        sanctioned: "Sanctioned",
        rejected: "Rejected",
        pendingKyc: "Pending KYC",
        inCredit: "In Credit",
        inApproval: "In Approval",
        customers: "Customers",
        pipeline: "Pipeline",
        myQueue: "My Queue",
        recentAudit: "Recent Audit",
      },
      common: {
        bank: "Bank",
        branch: "Branch",
        region: "Region",
        role: "Role",
        none: "None",
      },
      forms: {
        firstName: "First Name", middleName: "Middle Name", lastName: "Last Name",
        email: "Email", mobile: "Mobile", phone: "Phone", address: "Address",
        dob: "Date of Birth", gender: "Gender", maritalStatus: "Marital Status",
        male: "Male", female: "Female", other: "Other",
        single: "Single", married: "Married",
        pan: "PAN", aadhaar: "Aadhaar", gstin: "GSTIN",
        occupation: "Occupation", employerName: "Employer Name", employerType: "Employer Type",
        income: "Net Monthly Income", emi: "Existing EMI",
        constitution: "Constitution", individual: "Individual", proprietorship: "Proprietorship",
        partnership: "Partnership", privateLimited: "Private Limited", llp: "LLP",
        publicLimited: "Public Limited", trust: "Trust", society: "Society",
        loanType: "Loan Type", purpose: "Purpose", amount: "Amount",
        tenor: "Tenor (months)", interestRate: "Interest Rate (% p.a.)",
        propertyCost: "Property Cost", propertyLocation: "Property Location",
        rera: "RERA Number", builder: "Builder Name", ltv: "LTV %", propertyType: "Property Type",
        foir: "FOIR %",
        password: "Password", confirmPassword: "Confirm Password",
        employeeId: "Employee ID", department: "Department",
        relationship: "Relationship", spouse: "Spouse", father: "Father", mother: "Mother",
        son: "Son", daughter: "Daughter", brother: "Brother", sister: "Sister",
        creditPull: "Multi-Bureau Credit Pull",
        addCustomer: "Add Customer", saveChanges: "Save Changes", createUser: "Create User",
        required: "Required", optional: "Optional",
      },
      tabs: {
        kyc: "KYC", coApplicants: "Co-Applicants", documents: "Documents",
        fv: "Field Verification", cma: "CMA", cra: "CRA",
        assessment: "Assessment", security: "Security", aa: "Account Aggregator",
        stock: "Stock Statements", policy: "Policy", workflow: "Workflow",
        sFormat: "S-Format",
        cmaTitle: "CMA — Credit Monitoring Arrangement",
        cmaSubtitle: "Multi-period balance sheet, P&L, ratios, cash & funds flow.",
        cmaRun: "Run CMA Analysis", cmaSave: "Save CMA",
        cmaImport: "Auto Import from PDF", cmaDownload: "Download CMA",
        cmaBalanceSheet: "Balance Sheet", cmaIncome: "Income Statement",
        cmaRatios: "Ratios", cmaCashFlow: "Cash Flow", cmaFundsFlow: "Funds Flow",
        cmaPeriods: "Periods",
        securityTitle: "Security & Collateral",
        securitySubtitle: "Primary security, collateral, guarantors, and CGTMSE coverage.",
        securityAdd: "Add Security", securityType: "Type", securityValue: "Value (₹L)",
        securityDescription: "Description", securityCgtmse: "CGTMSE Coverage",
        securityCersai: "CERSAI Registration", securitySave: "Save",
        workflowTitle: "Workflow & Approvals",
        workflowSubtitle: "Stage transitions, audit trail, and approver decisions.",
        workflowCurrentStage: "Current Stage",
        workflowAuditTrail: "Audit Trail",
        workflowMoveTo: "Move to", workflowApprove: "Approve",
        workflowReject: "Reject", workflowRemarks: "Remarks",
        workflowSubmitForApproval: "Submit for Approval",
        policyTitle: "{{bank}} Policy Engine",
        policySubtitle: "Automated rule evaluation against your {{bank}} Loan Policy 2024 ruleset.",
        policyEvaluate: "Evaluate Now",
        sFormatTitle: "S-Format Sanction Proposal",
        sFormatGenerate: "Generate Draft", sFormatRegenerate: "Regenerate",
        sFormatLock: "Finalize & Lock", sFormatDownload: "Download PDF",
      },
    },
  },
  hi: {
    translation: {
      app: {
        tagline: "एकीकृत ऋण उत्पत्ति प्रणाली",
        loading: "लोड हो रहा है…",
        cancel: "रद्द करें",
        save: "सहेजें",
        submit: "जमा करें",
        confirm: "पुष्टि करें",
        delete: "हटाएँ",
        edit: "संपादित करें",
        search: "खोजें",
        download: "डाउनलोड",
        upload: "अपलोड",
        close: "बंद करें",
        next: "अगला",
        previous: "पिछला",
        new: "नया",
        actions: "क्रियाएँ",
        status: "स्थिति",
        active: "सक्रिय",
        disabled: "निष्क्रिय",
      },
      login: {
        title: "वापसी पर स्वागत है",
        subtitle: "अपने खाते में साइन इन करें",
        email: "ईमेल",
        password: "पासवर्ड",
        signin: "साइन इन",
        forgot: "पासवर्ड भूल गए?",
        demo: "डेमो खाते",
      },
      nav: {
        operations: "संचालन",
        governance: "शासन",
        insights: "अंतर्दृष्टि",
        administration: "प्रशासन",
        dashboard: "डैशबोर्ड",
        customers: "ग्राहक",
        applications: "आवेदन",
        drafts: "मेरे ड्राफ्ट",
        reportBuilder: "रिपोर्ट बिल्डर",
        foQueue: "एफओ क्यू",
        approvals: "अनुमोदन",
        fieldVerification: "क्षेत्र सत्यापन",
        documents: "दस्तावेज़",
        masterData: "मास्टर डेटा",
        users: "उपयोगकर्ता",
        settings: "सेटिंग्स",
        rules: "नीति नियम",
        themes: "थीम",
        notifications: "सूचनाएँ",
        superAdmin: "सुपर एडमिन",
        tenantOverview: "टेनेंट अवलोकन",
        onboardBank: "बैंक जोड़ें",
        allUsers: "सभी उपयोगकर्ता",
        appearance: "स्वरूप",
      },
      dashboard: {
        welcome: "स्वागत है",
        totalApplications: "कुल आवेदन",
        inProgress: "प्रगति में",
        sanctioned: "स्वीकृत",
        rejected: "अस्वीकृत",
        pendingKyc: "लंबित केवाईसी",
        inCredit: "क्रेडिट में",
        inApproval: "अनुमोदन में",
        customers: "ग्राहक",
        pipeline: "पाइपलाइन",
        myQueue: "मेरी सूची",
        recentAudit: "हाल का ऑडिट",
      },
      common: { bank: "बैंक", branch: "शाखा", region: "क्षेत्र", role: "भूमिका", none: "कोई नहीं" },
      forms: {
        firstName: "पहला नाम", middleName: "मध्य नाम", lastName: "उपनाम",
        email: "ईमेल", mobile: "मोबाइल", phone: "फ़ोन", address: "पता",
        dob: "जन्म तिथि", gender: "लिंग", maritalStatus: "वैवाहिक स्थिति",
        male: "पुरुष", female: "महिला", other: "अन्य",
        single: "अविवाहित", married: "विवाहित",
        pan: "पैन", aadhaar: "आधार", gstin: "जीएसटीआईएन",
        occupation: "व्यवसाय", employerName: "नियोक्ता का नाम", employerType: "नियोक्ता का प्रकार",
        income: "शुद्ध मासिक आय", emi: "मौजूदा ईएमआई",
        constitution: "गठन", individual: "व्यक्तिगत", proprietorship: "एकल स्वामित्व",
        partnership: "साझेदारी", privateLimited: "प्राइवेट लिमिटेड", llp: "एलएलपी",
        publicLimited: "पब्लिक लिमिटेड", trust: "ट्रस्ट", society: "सोसायटी",
        loanType: "ऋण प्रकार", purpose: "उद्देश्य", amount: "राशि",
        tenor: "अवधि (महीने)", interestRate: "ब्याज दर (% वार्षिक)",
        propertyCost: "संपत्ति की लागत", propertyLocation: "संपत्ति का स्थान",
        rera: "RERA संख्या", builder: "बिल्डर का नाम", ltv: "LTV %", propertyType: "संपत्ति का प्रकार",
        foir: "FOIR %",
        password: "पासवर्ड", confirmPassword: "पासवर्ड की पुष्टि करें",
        employeeId: "कर्मचारी आईडी", department: "विभाग",
        relationship: "संबंध", spouse: "जीवनसाथी", father: "पिता", mother: "माता",
        son: "बेटा", daughter: "बेटी", brother: "भाई", sister: "बहन",
        creditPull: "मल्टी-ब्यूरो क्रेडिट पुल",
        addCustomer: "ग्राहक जोड़ें", saveChanges: "परिवर्तन सहेजें", createUser: "उपयोगकर्ता बनाएँ",
        required: "आवश्यक", optional: "वैकल्पिक",
      },
      tabs: {
        kyc: "केवाईसी", coApplicants: "सह-आवेदक", documents: "दस्तावेज़",
        fv: "क्षेत्र सत्यापन", cma: "सीएमए", cra: "सीआरए",
        assessment: "मूल्यांकन", security: "सिक्योरिटी", aa: "अकाउंट एग्रीगेटर",
        stock: "स्टॉक स्टेटमेंट", policy: "नीति", workflow: "वर्कफ़्लो",
        sFormat: "एस-फॉर्मेट",
        cmaTitle: "सीएमए — क्रेडिट मॉनिटरिंग अरेंजमेंट",
        cmaSubtitle: "बहु-अवधि बैलेंस शीट, पीएंडएल, अनुपात, कैश एवं फंड फ्लो।",
        cmaRun: "सीएमए विश्लेषण चलाएँ", cmaSave: "सीएमए सहेजें",
        cmaImport: "पीडीएफ से एआई इम्पोर्ट", cmaDownload: "सीएमए डाउनलोड",
        cmaBalanceSheet: "बैलेंस शीट", cmaIncome: "आय विवरण",
        cmaRatios: "अनुपात", cmaCashFlow: "नकदी प्रवाह", cmaFundsFlow: "फंड फ्लो",
        cmaPeriods: "अवधियाँ",
        securityTitle: "सिक्योरिटी एवं संपार्श्विक",
        securitySubtitle: "प्राथमिक सिक्योरिटी, कोलैटरल, गारंटर, और सीजीटीएमएसई कवरेज।",
        securityAdd: "सिक्योरिटी जोड़ें", securityType: "प्रकार", securityValue: "मूल्य (₹L)",
        securityDescription: "विवरण", securityCgtmse: "सीजीटीएमएसई कवरेज",
        securityCersai: "सेरसाई पंजीकरण", securitySave: "सहेजें",
        workflowTitle: "वर्कफ़्लो एवं अनुमोदन",
        workflowSubtitle: "चरण परिवर्तन, ऑडिट ट्रेल, और अनुमोदक निर्णय।",
        workflowCurrentStage: "वर्तमान चरण",
        workflowAuditTrail: "ऑडिट ट्रेल",
        workflowMoveTo: "ले जाएँ", workflowApprove: "अनुमोदित करें",
        workflowReject: "अस्वीकार करें", workflowRemarks: "टिप्पणियाँ",
        workflowSubmitForApproval: "अनुमोदन के लिए जमा करें",
        policyTitle: "{{bank}} नीति इंजन",
        policySubtitle: "आपके {{bank}} ऋण नीति 2024 नियमों के अनुसार स्वचालित मूल्यांकन।",
        policyEvaluate: "अभी मूल्यांकन करें",
        sFormatTitle: "एस-फॉर्मेट स्वीकृति प्रस्ताव",
        sFormatGenerate: "ड्राफ्ट तैयार करें", sFormatRegenerate: "पुनः तैयार करें",
        sFormatLock: "अंतिम रूप दें और लॉक करें", sFormatDownload: "पीडीएफ डाउनलोड",
      },
    },
  },
  mr: {
    translation: {
      app: {
        tagline: "एकात्मिक कर्ज उत्पत्ती प्रणाली",
        loading: "लोड होत आहे…",
        cancel: "रद्द करा",
        save: "जतन करा",
        submit: "सादर करा",
        confirm: "पुष्टी करा",
        delete: "हटवा",
        edit: "संपादन",
        search: "शोधा",
        download: "डाउनलोड",
        upload: "अपलोड",
        close: "बंद करा",
        next: "पुढे",
        previous: "मागे",
        new: "नवीन",
        actions: "क्रिया",
        status: "स्थिती",
        active: "सक्रिय",
        disabled: "निष्क्रिय",
      },
      login: {
        title: "पुन्हा स्वागत आहे",
        subtitle: "तुमच्या खात्यात साइन इन करा",
        email: "ईमेल",
        password: "पासवर्ड",
        signin: "साइन इन",
        forgot: "पासवर्ड विसरला?",
        demo: "डेमो खाती",
      },
      nav: {
        operations: "कामकाज",
        governance: "शासन",
        insights: "अंतर्दृष्टी",
        administration: "प्रशासन",
        dashboard: "डॅशबोर्ड",
        customers: "ग्राहक",
        applications: "अर्ज",
        drafts: "माझे ड्राफ्ट",
        reportBuilder: "रिपोर्ट बिल्डर",
        approvals: "मंजुरी",
        fieldVerification: "क्षेत्र पडताळणी",
        documents: "कागदपत्रे",
        masterData: "मास्टर डेटा",
        users: "वापरकर्ते",
        settings: "सेटिंग्ज",
        rules: "धोरण नियम",
        themes: "थीम्स",
        notifications: "सूचना",
        superAdmin: "सुपर अॅडमिन",
        tenantOverview: "टेनंट आढावा",
        onboardBank: "बँक जोडा",
        allUsers: "सर्व वापरकर्ते",
        appearance: "देखावा",
      },
      dashboard: {
        welcome: "स्वागत आहे",
        totalApplications: "एकूण अर्ज",
        inProgress: "प्रगतीत",
        sanctioned: "मंजूर",
        rejected: "नाकारले",
        pendingKyc: "केवायसी प्रलंबित",
        inCredit: "क्रेडिटमध्ये",
        inApproval: "मंजुरीसाठी",
        customers: "ग्राहक",
        pipeline: "पाइपलाइन",
        myQueue: "माझी रांग",
        recentAudit: "अलीकडील ऑडिट",
      },
      common: { bank: "बँक", branch: "शाखा", region: "प्रदेश", role: "भूमिका", none: "काहीही नाही" },
      forms: {
        firstName: "प्रथम नाव", middleName: "मधले नाव", lastName: "आडनाव",
        email: "ईमेल", mobile: "मोबाईल", phone: "फोन", address: "पत्ता",
        dob: "जन्म तारीख", gender: "लिंग", maritalStatus: "वैवाहिक स्थिती",
        male: "पुरुष", female: "स्त्री", other: "इतर",
        single: "अविवाहित", married: "विवाहित",
        pan: "पॅन", aadhaar: "आधार", gstin: "जीएसटीआयएन",
        occupation: "व्यवसाय", employerName: "नियोक्त्याचे नाव", employerType: "नियोक्त्याचा प्रकार",
        income: "निव्वळ मासिक उत्पन्न", emi: "विद्यमान EMI",
        constitution: "घटना", individual: "वैयक्तिक", proprietorship: "एकल मालकी",
        partnership: "भागीदारी", privateLimited: "प्रायव्हेट लिमिटेड", llp: "एलएलपी",
        publicLimited: "पब्लिक लिमिटेड", trust: "ट्रस्ट", society: "सोसायटी",
        loanType: "कर्जाचा प्रकार", purpose: "उद्देश", amount: "रक्कम",
        tenor: "मुदत (महिने)", interestRate: "व्याज दर (% वार्षिक)",
        propertyCost: "मालमत्तेची किंमत", propertyLocation: "मालमत्तेचे ठिकाण",
        rera: "RERA क्रमांक", builder: "बिल्डरचे नाव", ltv: "LTV %", propertyType: "मालमत्तेचा प्रकार",
        foir: "FOIR %",
        password: "पासवर्ड", confirmPassword: "पासवर्डची पुष्टी",
        employeeId: "कर्मचारी ID", department: "विभाग",
        relationship: "नाते", spouse: "जोडीदार", father: "वडील", mother: "आई",
        son: "मुलगा", daughter: "मुलगी", brother: "भाऊ", sister: "बहीण",
        creditPull: "मल्टी-ब्युरो क्रेडिट पुल",
        addCustomer: "ग्राहक जोडा", saveChanges: "बदल जतन करा", createUser: "वापरकर्ता तयार करा",
        required: "आवश्यक", optional: "वैकल्पिक",
      },
      tabs: {
        kyc: "केवायसी", coApplicants: "सह-अर्जदार", documents: "दस्तऐवज",
        fv: "क्षेत्र पडताळणी", cma: "सीएमए", cra: "सीआरए",
        assessment: "मूल्यांकन", security: "सिक्युरिटी", aa: "अकाउंट एग्रीगेटर",
        stock: "स्टॉक स्टेटमेंट", policy: "धोरण", workflow: "वर्कफ्लो",
        sFormat: "एस-फॉरमॅट",
        cmaTitle: "सीएमए — क्रेडिट मॉनिटरिंग अरेंजमेंट",
        cmaSubtitle: "बहु-कालावधी ताळेबंद, नफा-तोटा, गुणोत्तरे, रोख व निधी प्रवाह.",
        cmaRun: "सीएमए विश्लेषण चालवा", cmaSave: "सीएमए जतन करा",
        cmaImport: "PDF वरून AI आयात", cmaDownload: "सीएमए डाउनलोड",
        cmaBalanceSheet: "ताळेबंद", cmaIncome: "उत्पन्न विवरण",
        cmaRatios: "गुणोत्तरे", cmaCashFlow: "रोख प्रवाह", cmaFundsFlow: "निधी प्रवाह",
        cmaPeriods: "कालावधी",
        securityTitle: "सिक्युरिटी आणि तारण",
        securitySubtitle: "प्राथमिक सिक्युरिटी, तारण, हमीदार, आणि CGTMSE कव्हरेज.",
        securityAdd: "सिक्युरिटी जोडा", securityType: "प्रकार", securityValue: "मूल्य (₹L)",
        securityDescription: "वर्णन", securityCgtmse: "CGTMSE कव्हरेज",
        securityCersai: "CERSAI नोंदणी", securitySave: "जतन करा",
        workflowTitle: "वर्कफ्लो आणि मंजुरी",
        workflowSubtitle: "टप्पा बदल, ऑडिट ट्रेल, आणि मंजुरीचे निर्णय.",
        workflowCurrentStage: "सध्याचा टप्पा",
        workflowAuditTrail: "ऑडिट ट्रेल",
        workflowMoveTo: "स्थानांतरित करा", workflowApprove: "मंजूर",
        workflowReject: "नाकारा", workflowRemarks: "शेरा",
        workflowSubmitForApproval: "मंजुरीसाठी पाठवा",
        policyTitle: "{{bank}} धोरण इंजिन",
        policySubtitle: "आपल्या {{bank}} कर्ज धोरण 2024 नियमांनुसार स्वयंचलित मूल्यांकन.",
        policyEvaluate: "आता मूल्यांकन करा",
        sFormatTitle: "एस-फॉरमॅट मंजुरी प्रस्ताव",
        sFormatGenerate: "ड्राफ्ट तयार करा", sFormatRegenerate: "पुन्हा तयार करा",
        sFormatLock: "अंतिम करा आणि लॉक", sFormatDownload: "PDF डाउनलोड",
      },
    },
  },
  as: {
    translation: {
      app: {
        tagline: "একত্ৰিত ঋণ সৃষ্টি ব্যৱস্থা",
        loading: "ল’ড হৈ আছে…",
        cancel: "বাতিল",
        save: "সংৰক্ষণ",
        submit: "জমা",
        confirm: "নিশ্চিত কৰক",
        delete: "মচক",
        edit: "সম্পাদনা",
        search: "বিচাৰক",
        download: "ডাউনলোড",
        upload: "আপলোড",
        close: "বন্ধ কৰক",
        next: "পৰৱৰ্তী",
        previous: "পূৰ্বৱৰ্তী",
        new: "নতুন",
        actions: "ক্ৰিয়া",
        status: "স্থিতি",
        active: "সক্ৰিয়",
        disabled: "নিষ্ক্ৰিয়",
      },
      login: {
        title: "পুনৰ স্বাগতম",
        subtitle: "আপোনাৰ একাউণ্টত ছাইন ইন কৰক",
        email: "ইমেইল",
        password: "পাছৱৰ্ড",
        signin: "ছাইন ইন",
        forgot: "পাছৱৰ্ড পাহৰিছে?",
        demo: "ডেমো একাউণ্ট",
      },
      nav: {
        operations: "অপাৰেচন",
        governance: "শাসন",
        insights: "অন্তৰ্দৃষ্টি",
        administration: "প্ৰশাসন",
        dashboard: "ডেছবৰ্ড",
        customers: "গ্ৰাহক",
        applications: "আবেদন",
        drafts: "মোৰ ড্ৰাফ্ট",
        reportBuilder: "ৰিপোৰ্ট বিল্ডাৰ",
        approvals: "অনুমোদন",
        fieldVerification: "ক্ষেত্ৰ সত্যাপন",
        documents: "নথি",
        masterData: "মাষ্টাৰ ডেটা",
        users: "ব্যৱহাৰকাৰী",
        settings: "ছেটিংছ",
        rules: "নীতি নিয়ম",
        themes: "থীম",
        notifications: "জাননী",
        superAdmin: "চুপাৰ এডমিন",
        tenantOverview: "টেনেণ্ট আভাস",
        onboardBank: "বেংক যোগ কৰক",
        allUsers: "সকলো ব্যৱহাৰকাৰী",
        appearance: "ৰূপ",
      },
      dashboard: {
        welcome: "স্বাগতম",
        totalApplications: "মুঠ আবেদন",
        inProgress: "চলি আছে",
        sanctioned: "অনুমোদিত",
        rejected: "প্ৰত্যাখ্যান",
        pendingKyc: "KYC বাকী",
        inCredit: "ক্ৰেডিটত",
        inApproval: "অনুমোদনত",
        customers: "গ্ৰাহক",
        pipeline: "পাইপলাইন",
        myQueue: "মোৰ শাৰী",
        recentAudit: "শেহতীয়া অডিট",
      },
      common: { bank: "বেংক", branch: "শাখা", region: "অঞ্চল", role: "ভূমিকা", none: "একো নাই" },
      forms: {
        firstName: "প্ৰথম নাম", middleName: "মধ্যম নাম", lastName: "ওপনাম",
        email: "ইমেইল", mobile: "ম’বাইল", phone: "ফোন", address: "ঠিকনা",
        dob: "জন্ম তাৰিখ", gender: "লিংগ", maritalStatus: "বৈবাহিক স্থিতি",
        male: "পুৰুষ", female: "মহিলা", other: "অন্য",
        single: "অবিবাহিত", married: "বিবাহিত",
        pan: "প্যান", aadhaar: "আধাৰ", gstin: "জিএছটিআইএন",
        occupation: "ব্যৱসায়", employerName: "নিয়োজকৰ নাম", employerType: "নিয়োজকৰ ধৰণ",
        income: "মাহিলী আয়", emi: "বৰ্তমান ইএমআই",
        constitution: "গঠন", individual: "ব্যক্তিগত", proprietorship: "এক মালিকীয়",
        partnership: "অংশীদাৰিত্ব", privateLimited: "প্ৰাইভেট লিমিটেড", llp: "এলএলপি",
        publicLimited: "পাব্লিক লিমিটেড", trust: "ট্ৰাষ্ট", society: "সমিতি",
        loanType: "ঋণৰ ধৰণ", purpose: "উদ্দেশ্য", amount: "পৰিমাণ",
        tenor: "সময় (মাহ)", interestRate: "সুতৰ হাৰ (% বছৰিয়া)",
        propertyCost: "সম্পত্তিৰ মূল্য", propertyLocation: "সম্পত্তিৰ স্থান",
        rera: "RERA নম্বৰ", builder: "নিৰ্মাতাৰ নাম", ltv: "LTV %", propertyType: "সম্পত্তিৰ ধৰণ",
        foir: "FOIR %",
        password: "পাছৱৰ্ড", confirmPassword: "পাছৱৰ্ড নিশ্চিতকৰণ",
        employeeId: "কৰ্মচাৰী আইডি", department: "বিভাগ",
        relationship: "সম্পৰ্ক", spouse: "জীৱনসংগী", father: "পিতা", mother: "মাতৃ",
        son: "পুত্ৰ", daughter: "কন্যা", brother: "ভাই", sister: "ভনী",
        creditPull: "মাল্টি-ব্যুৰো ক্ৰেডিট পুল",
        addCustomer: "গ্ৰাহক যোগ কৰক", saveChanges: "সলনি সংৰক্ষণ কৰক", createUser: "ব্যৱহাৰকাৰী সৃষ্টি কৰক",
        required: "প্ৰয়োজনীয়", optional: "বৈকল্পিক",
      },
      tabs: {
        kyc: "কেৱাইচি", coApplicants: "সহ-আবেদনকাৰী", documents: "নথি",
        fv: "ক্ষেত্ৰ সত্যাপন", cma: "চিএমএ", cra: "চিআৰএ",
        assessment: "মূল্যায়ন", security: "ছেকিউৰিটি", aa: "একাউণ্ট এগ্ৰিগেটৰ",
        stock: "ষ্টক ষ্টেটমেণ্ট", policy: "নীতি", workflow: "ৱৰ্কফ্ল’",
        sFormat: "এছ-ফৰ্মেট",
        cmaTitle: "চিএমএ — Credit Monitoring Arrangement",
        cmaSubtitle: "বহু-সময়সীমাৰ Balance Sheet, P&L, অনুপাত, নগদ আৰু পুঁজি প্ৰবাহ।",
        cmaRun: "চিএমএ বিশ্লেষণ চলাওক", cmaSave: "চিএমএ সংৰক্ষণ",
        cmaImport: "PDF-ৰ পৰা AI Import", cmaDownload: "চিএমএ ডাউনল’ড",
        cmaBalanceSheet: "Balance Sheet", cmaIncome: "আয় বিৱৰণ",
        cmaRatios: "অনুপাত", cmaCashFlow: "Cash Flow", cmaFundsFlow: "Funds Flow",
        cmaPeriods: "সময়সীমা",
        securityTitle: "ছেকিউৰিটি আৰু জামিন",
        securitySubtitle: "প্ৰাথমিক ছেকিউৰিটি, কোলেটাৰেল, গেৰাণ্টৰ আৰু CGTMSE Cover।",
        securityAdd: "ছেকিউৰিটি যোগ কৰক", securityType: "প্ৰকাৰ", securityValue: "মূল্য (₹L)",
        securityDescription: "বিৱৰণ", securityCgtmse: "CGTMSE Cover",
        securityCersai: "CERSAI পঞ্জীয়ন", securitySave: "সংৰক্ষণ",
        workflowTitle: "ৱৰ্কফ্ল’ আৰু অনুমোদন",
        workflowSubtitle: "স্তৰ পৰিৱৰ্তন, Audit Trail আৰু অনুমোদনৰ সিদ্ধান্ত।",
        workflowCurrentStage: "বৰ্তমান স্তৰ",
        workflowAuditTrail: "Audit Trail",
        workflowMoveTo: "স্থানান্তৰ", workflowApprove: "অনুমোদন",
        workflowReject: "প্ৰত্যাখ্যান", workflowRemarks: "মন্তব্য",
        workflowSubmitForApproval: "অনুমোদনৰ বাবে দাখিল কৰক",
        policyTitle: "{{bank}} নীতি Engine",
        policySubtitle: "আপোনাৰ {{bank}} ঋণ নীতি 2024 অনুযায়ী স্বয়ংক্ৰিয় মূল্যায়ন।",
        policyEvaluate: "এতিয়াই মূল্যায়ন কৰক",
        sFormatTitle: "এছ-ফৰ্মেট অনুমোদন প্ৰস্তাৱ",
        sFormatGenerate: "Draft সৃষ্টি কৰক", sFormatRegenerate: "পুনঃ সৃষ্টি",
        sFormatLock: "চূড়ান্ত আৰু Lock", sFormatDownload: "PDF Download",
      },
    },
  },
  bn: {
    translation: {
      app: {
        tagline: "একীভূত ঋণ সৃষ্টি সিস্টেম",
        loading: "লোড হচ্ছে…",
        cancel: "বাতিল",
        save: "সংরক্ষণ",
        submit: "জমা",
        confirm: "নিশ্চিত করুন",
        delete: "মুছুন",
        edit: "সম্পাদনা",
        search: "অনুসন্ধান",
        download: "ডাউনলোড",
        upload: "আপলোড",
        close: "বন্ধ",
        next: "পরবর্তী",
        previous: "পূর্ববর্তী",
        new: "নতুন",
        actions: "ক্রিয়াকলাপ",
        status: "অবস্থা",
        active: "সক্রিয়",
        disabled: "নিষ্ক্রিয়",
      },
      login: {
        title: "আবার স্বাগতম",
        subtitle: "আপনার অ্যাকাউন্টে সাইন ইন করুন",
        email: "ইমেল",
        password: "পাসওয়ার্ড",
        signin: "সাইন ইন",
        forgot: "পাসওয়ার্ড ভুলে গেছেন?",
        demo: "ডেমো অ্যাকাউন্ট",
      },
      nav: {
        operations: "অপারেশনস",
        governance: "শাসন",
        insights: "অন্তর্দৃষ্টি",
        administration: "প্রশাসন",
        dashboard: "ড্যাশবোর্ড",
        customers: "গ্রাহক",
        applications: "আবেদন",
        drafts: "আমার খসড়া",
        reportBuilder: "রিপোর্ট বিল্ডার",
        approvals: "অনুমোদন",
        fieldVerification: "মাঠ যাচাই",
        documents: "ডকুমেন্ট",
        masterData: "মাস্টার ডেটা",
        users: "ব্যবহারকারী",
        settings: "সেটিংস",
        rules: "নীতি নিয়ম",
        themes: "থিম",
        notifications: "বিজ্ঞপ্তি",
        superAdmin: "সুপার অ্যাডমিন",
        tenantOverview: "টেন্যান্ট ওভারভিউ",
        onboardBank: "ব্যাঙ্ক যোগ করুন",
        allUsers: "সব ব্যবহারকারী",
        appearance: "চেহারা",
      },
      dashboard: {
        welcome: "স্বাগতম",
        totalApplications: "মোট আবেদন",
        inProgress: "চলমান",
        sanctioned: "অনুমোদিত",
        rejected: "প্রত্যাখ্যাত",
        pendingKyc: "KYC বাকি",
        inCredit: "ক্রেডিটে",
        inApproval: "অনুমোদনে",
        customers: "গ্রাহক",
        pipeline: "পাইপলাইন",
        myQueue: "আমার সারি",
        recentAudit: "সাম্প্রতিক নিরীক্ষা",
      },
      common: { bank: "ব্যাঙ্ক", branch: "শাখা", region: "অঞ্চল", role: "ভূমিকা", none: "কিছুই নয়" },
      forms: {
        firstName: "প্রথম নাম", middleName: "মধ্য নাম", lastName: "শেষ নাম",
        email: "ইমেল", mobile: "মোবাইল", phone: "ফোন", address: "ঠিকানা",
        dob: "জন্ম তারিখ", gender: "লিঙ্গ", maritalStatus: "বৈবাহিক অবস্থা",
        male: "পুরুষ", female: "মহিলা", other: "অন্য",
        single: "অবিবাহিত", married: "বিবাহিত",
        pan: "প্যান", aadhaar: "আধার", gstin: "জিএসটিআইএন",
        occupation: "পেশা", employerName: "নিয়োগকর্তার নাম", employerType: "নিয়োগকর্তার ধরন",
        income: "নিট মাসিক আয়", emi: "বর্তমান ইএমআই",
        constitution: "গঠন", individual: "ব্যক্তিগত", proprietorship: "একক মালিকানা",
        partnership: "অংশীদারিত্ব", privateLimited: "প্রাইভেট লিমিটেড", llp: "এলএলপি",
        publicLimited: "পাবলিক লিমিটেড", trust: "ট্রাস্ট", society: "সমিতি",
        loanType: "ঋণের ধরন", purpose: "উদ্দেশ্য", amount: "পরিমাণ",
        tenor: "মেয়াদ (মাস)", interestRate: "সুদের হার (% বার্ষিক)",
        propertyCost: "সম্পত্তির মূল্য", propertyLocation: "সম্পত্তির অবস্থান",
        rera: "RERA নম্বর", builder: "বিল্ডারের নাম", ltv: "LTV %", propertyType: "সম্পত্তির ধরন",
        foir: "FOIR %",
        password: "পাসওয়ার্ড", confirmPassword: "পাসওয়ার্ড নিশ্চিত করুন",
        employeeId: "কর্মচারী আইডি", department: "বিভাগ",
        relationship: "সম্পর্ক", spouse: "জীবনসঙ্গী", father: "পিতা", mother: "মাতা",
        son: "ছেলে", daughter: "মেয়ে", brother: "ভাই", sister: "বোন",
        creditPull: "মাল্টি-ব্যুরো ক্রেডিট পুল",
        addCustomer: "গ্রাহক যোগ করুন", saveChanges: "পরিবর্তন সংরক্ষণ", createUser: "ব্যবহারকারী তৈরি করুন",
        required: "আবশ্যক", optional: "বৈকল্পিক",
      },
      tabs: {
        kyc: "কেওয়াইসি", coApplicants: "সহ-আবেদনকারী", documents: "নথি",
        fv: "মাঠ যাচাই", cma: "সিএমএ", cra: "সিআরএ",
        assessment: "মূল্যায়ন", security: "সিকিউরিটি", aa: "অ্যাকাউন্ট অ্যাগ্রিগেটর",
        stock: "স্টক স্টেটমেন্ট", policy: "নীতি", workflow: "ওয়ার্কফ্লো",
        sFormat: "এস-ফরম্যাট",
        cmaTitle: "সিএমএ — Credit Monitoring Arrangement",
        cmaSubtitle: "বহু-সময়ের Balance Sheet, P&L, অনুপাত, নগদ ও তহবিল প্রবাহ।",
        cmaRun: "সিএমএ বিশ্লেষণ চালান", cmaSave: "সিএমএ সংরক্ষণ",
        cmaImport: "PDF থেকে AI Import", cmaDownload: "সিএমএ ডাউনলোড",
        cmaBalanceSheet: "Balance Sheet", cmaIncome: "আয় বিবরণী",
        cmaRatios: "অনুপাত", cmaCashFlow: "Cash Flow", cmaFundsFlow: "Funds Flow",
        cmaPeriods: "সময়কাল",
        securityTitle: "সিকিউরিটি ও জামানত",
        securitySubtitle: "প্রাথমিক সিকিউরিটি, কোল্যাটারাল, গ্যারান্টর ও CGTMSE Cover।",
        securityAdd: "সিকিউরিটি যোগ করুন", securityType: "ধরন", securityValue: "মূল্য (₹L)",
        securityDescription: "বিবরণ", securityCgtmse: "CGTMSE Cover",
        securityCersai: "CERSAI নিবন্ধন", securitySave: "সংরক্ষণ",
        workflowTitle: "ওয়ার্কফ্লো ও অনুমোদন",
        workflowSubtitle: "পর্যায় পরিবর্তন, Audit Trail ও অনুমোদনের সিদ্ধান্ত।",
        workflowCurrentStage: "বর্তমান পর্যায়",
        workflowAuditTrail: "Audit Trail",
        workflowMoveTo: "স্থানান্তর", workflowApprove: "অনুমোদন",
        workflowReject: "প্রত্যাখ্যান", workflowRemarks: "মন্তব্য",
        workflowSubmitForApproval: "অনুমোদনের জন্য জমা দিন",
        policyTitle: "{{bank}} নীতি Engine",
        policySubtitle: "আপনার {{bank}} ঋণ নীতি 2024 অনুযায়ী স্বয়ংক্রিয় মূল্যায়ন।",
        policyEvaluate: "এখনই মূল্যায়ন করুন",
        sFormatTitle: "এস-ফরম্যাট অনুমোদন প্রস্তাব",
        sFormatGenerate: "Draft তৈরি করুন", sFormatRegenerate: "পুনরায় তৈরি",
        sFormatLock: "চূড়ান্ত ও Lock", sFormatDownload: "PDF Download",
      },
    },
  },
  ne: {
    translation: {
      app: {
        tagline: "एकीकृत ऋण उत्पत्ति प्रणाली",
        loading: "लोड हुँदै छ…",
        cancel: "रद्द गर्नुहोस्",
        save: "बचत गर्नुहोस्",
        submit: "पेस गर्नुहोस्",
        confirm: "पुष्टि गर्नुहोस्",
        delete: "मेटाउनुहोस्",
        edit: "सम्पादन",
        search: "खोज्नुहोस्",
        download: "डाउनलोड",
        upload: "अपलोड",
        close: "बन्द गर्नुहोस्",
        next: "अर्को",
        previous: "अघिल्लो",
        new: "नयाँ",
        actions: "कार्यहरू",
        status: "स्थिति",
        active: "सक्रिय",
        disabled: "निष्क्रिय",
      },
      login: {
        title: "पुनः स्वागत छ",
        subtitle: "तपाईंको खातामा साइन इन गर्नुहोस्",
        email: "इमेल",
        password: "पासवर्ड",
        signin: "साइन इन",
        forgot: "पासवर्ड बिर्सनु भयो?",
        demo: "डेमो खाताहरू",
      },
      nav: {
        operations: "सञ्चालन",
        governance: "शासन",
        insights: "अन्तर्दृष्टि",
        administration: "प्रशासन",
        dashboard: "ड्यासबोर्ड",
        customers: "ग्राहक",
        applications: "आवेदन",
        drafts: "मेरा ड्राफ्ट",
        reportBuilder: "रिपोर्ट बिल्डर",
        approvals: "स्वीकृति",
        fieldVerification: "क्षेत्र प्रमाणीकरण",
        documents: "कागजात",
        masterData: "मास्टर डाटा",
        users: "प्रयोगकर्ता",
        settings: "सेटिङ्स",
        rules: "नीति नियम",
        themes: "थिमहरू",
        notifications: "सूचनाहरू",
        superAdmin: "सुपर एडमिन",
        tenantOverview: "टेनेन्ट सिंहावलोकन",
        onboardBank: "बैंक थप्नुहोस्",
        allUsers: "सबै प्रयोगकर्ता",
        appearance: "रूपरङ",
      },
      dashboard: {
        welcome: "स्वागत छ",
        totalApplications: "कुल आवेदन",
        inProgress: "प्रगतिमा",
        sanctioned: "स्वीकृत",
        rejected: "अस्वीकृत",
        pendingKyc: "KYC बाँकी",
        inCredit: "क्रेडिटमा",
        inApproval: "स्वीकृतिमा",
        customers: "ग्राहक",
        pipeline: "पाइपलाइन",
        myQueue: "मेरो लाइन",
        recentAudit: "हालैको अडिट",
      },
      common: { bank: "बैंक", branch: "शाखा", region: "क्षेत्र", role: "भूमिका", none: "कुनै पनि छैन" },
      forms: {
        firstName: "पहिलो नाम", middleName: "बीचको नाम", lastName: "थर",
        email: "इमेल", mobile: "मोबाइल", phone: "फोन", address: "ठेगाना",
        dob: "जन्म मिति", gender: "लिङ्ग", maritalStatus: "वैवाहिक स्थिति",
        male: "पुरुष", female: "महिला", other: "अन्य",
        single: "अविवाहित", married: "विवाहित",
        pan: "प्यान", aadhaar: "आधार", gstin: "जिएसटीआईएन",
        occupation: "पेशा", employerName: "रोजगारदाताको नाम", employerType: "रोजगारदाताको प्रकार",
        income: "खुद मासिक आम्दानी", emi: "हालको EMI",
        constitution: "गठन", individual: "व्यक्तिगत", proprietorship: "एकलौटी",
        partnership: "साझेदारी", privateLimited: "प्राइभेट लिमिटेड", llp: "एलएलपी",
        publicLimited: "पब्लिक लिमिटेड", trust: "ट्रस्ट", society: "समाज",
        loanType: "ऋण प्रकार", purpose: "उद्देश्य", amount: "रकम",
        tenor: "अवधि (महिना)", interestRate: "ब्याज दर (% वार्षिक)",
        propertyCost: "सम्पत्तिको लागत", propertyLocation: "सम्पत्तिको स्थान",
        rera: "RERA नम्बर", builder: "निर्माताको नाम", ltv: "LTV %", propertyType: "सम्पत्ति प्रकार",
        foir: "FOIR %",
        password: "पासवर्ड", confirmPassword: "पासवर्ड पुष्टि",
        employeeId: "कर्मचारी आईडी", department: "विभाग",
        relationship: "नाता", spouse: "जीवनसाथी", father: "बुबा", mother: "आमा",
        son: "छोरा", daughter: "छोरी", brother: "दाजुभाइ", sister: "दिदीबहिनी",
        creditPull: "बहु-ब्युरो क्रेडिट पुल",
        addCustomer: "ग्राहक थप्नुहोस्", saveChanges: "परिवर्तन सेभ गर्नुहोस्", createUser: "प्रयोगकर्ता सिर्जना गर्नुहोस्",
        required: "आवश्यक", optional: "वैकल्पिक",
      },
      tabs: {
        kyc: "केवाईसी", coApplicants: "सह-आवेदक", documents: "कागजात",
        fv: "क्षेत्र प्रमाणीकरण", cma: "सीएमए", cra: "सीआरए",
        assessment: "मूल्याङ्कन", security: "सुरक्षा", aa: "खाता एग्रिगेटर",
        stock: "स्टक स्टेटमेन्ट", policy: "नीति", workflow: "वर्कफ्लो",
        sFormat: "एस-फरम्याट",
        cmaTitle: "सीएमए — Credit Monitoring Arrangement",
        cmaSubtitle: "बहु-अवधि Balance Sheet, P&L, अनुपात, नगद र कोष प्रवाह।",
        cmaRun: "सीएमए विश्लेषण चलाउनुहोस्", cmaSave: "सीएमए सेभ",
        cmaImport: "PDF बाट AI आयात", cmaDownload: "सीएमए डाउनलोड",
        cmaBalanceSheet: "Balance Sheet", cmaIncome: "आय विवरण",
        cmaRatios: "अनुपात", cmaCashFlow: "Cash Flow", cmaFundsFlow: "Funds Flow",
        cmaPeriods: "अवधिहरू",
        securityTitle: "सुरक्षा र धरौटी",
        securitySubtitle: "प्राथमिक सुरक्षा, धरौटी, जमानती र CGTMSE Cover।",
        securityAdd: "सुरक्षा थप्नुहोस्", securityType: "प्रकार", securityValue: "मूल्य (₹L)",
        securityDescription: "विवरण", securityCgtmse: "CGTMSE Cover",
        securityCersai: "CERSAI दर्ता", securitySave: "सेभ",
        workflowTitle: "वर्कफ्लो र स्वीकृति",
        workflowSubtitle: "चरण परिवर्तन, Audit Trail र स्वीकृति निर्णय।",
        workflowCurrentStage: "हालको चरण",
        workflowAuditTrail: "Audit Trail",
        workflowMoveTo: "स्थानान्तरण", workflowApprove: "स्वीकृत",
        workflowReject: "अस्वीकार", workflowRemarks: "टिप्पणी",
        workflowSubmitForApproval: "स्वीकृतिको लागि पेस गर्नुहोस्",
        policyTitle: "{{bank}} नीति Engine",
        policySubtitle: "तपाईंको {{bank}} ऋण नीति 2024 अनुसार स्वचालित मूल्याङ्कन।",
        policyEvaluate: "अहिले मूल्याङ्कन गर्नुहोस्",
        sFormatTitle: "एस-फरम्याट स्वीकृति प्रस्ताव",
        sFormatGenerate: "Draft सिर्जना", sFormatRegenerate: "पुनः सिर्जना",
        sFormatLock: "अन्तिम र Lock", sFormatDownload: "PDF Download",
      },
    },
  },
};

export const LANGUAGES = [
  { code: "en", label: "English", native: "English" },
  { code: "hi", label: "Hindi", native: "हिन्दी" },
  { code: "mr", label: "Marathi", native: "मराठी" },
  { code: "as", label: "Assamese", native: "অসমীয়া" },
  { code: "bn", label: "Bengali", native: "বাংলা" },
  { code: "ne", label: "Nepali", native: "नेपाली" },
];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    lng: "en", // Default language: English
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "acas_lang",
    },
  });

export default i18n;
